import { AbstractActionManager, Action, ActionManager, Animation, ExecuteCodeAction, Mesh } from '@babylonjs/core';
import { AdvancedDynamicTexture, Rectangle, TextBlock } from '@babylonjs/gui/2D';
import { ITooltipStyle, TooltipPosition, TooltipVisibility } from 'Framework/Components/Controls/XView/Core';
import { ColorHelper } from 'Framework/Helpers/ColorHelper';

export class Tooltip extends Rectangle {
    public constructor(
        text: string,
        advancedTexture: AdvancedDynamicTexture,
        anchor: Mesh,
        actionManager: AbstractActionManager,
        options: ITooltipStyle
    ) {
        super();

        this._anchor = anchor;
        this._actionManager = actionManager;
        this._guiTexture = advancedTexture;

        this.adaptWidthToChildren = true;
        this.adaptHeightToChildren = true;
        this.thickness = 1;
        this.background = '#000000AA';

        this._textblock = new TextBlock();
        this._textblock.text = text;
        this._textblock.resizeToFit = true;
        this._textblock.paddingBottomInPixels = 5;
        this._textblock.paddingLeftInPixels = 5;
        this._textblock.paddingRightInPixels = 5;
        this._textblock.paddingTopInPixels = 5;

        this.addControl(this._textblock);
        advancedTexture.addControl(this);

        this.linkWithMesh(anchor);

        this.options = options;
    }

    private _setPosition(options: ITooltipStyle): void {
        const hotspotMeshSize: number = this._anchor.getBoundingInfo().boundingSphere.radius;

        const tooltipHeight: number = this._getHeight();
        const tooltipWidth: number = this._getWidth();

        let offsetX = (tooltipWidth / 2) + hotspotMeshSize;
        let offsetY = (tooltipHeight / 2) + hotspotMeshSize;

        switch (options.position) {
            case TooltipPosition.Top:
                this.linkOffsetY = -offsetY;
                break;
            case TooltipPosition.Bottom:
                this.linkOffsetY = offsetY;
                break;
            case TooltipPosition.Left:
                this.linkOffsetX = -offsetX;
                break;
            case TooltipPosition.Right:
                this.linkOffsetX = offsetX;
                break;
        }
    }

    private _bind(actionManager: AbstractActionManager): void {
        const onPointerOverAction = new ExecuteCodeAction(
            ActionManager.OnPointerOverTrigger,
            _ => {
                this.isVisible = true;
                this.linkedMesh.getScene().beginDirectAnimation(this, Tooltip.animations, 0, 30);
            }
        );

        const onPointerOutAction = new ExecuteCodeAction(
            ActionManager.OnPointerOutTrigger,
            _ => {
                this.linkedMesh.getScene().stopAnimation(this);
                this.isVisible = false;
            }
        );

        actionManager.registerAction(onPointerOverAction);
        actionManager.registerAction(onPointerOutAction);

        this._actions.push(onPointerOverAction, onPointerOutAction);
    }

    private _unbind(actionManager: AbstractActionManager): void {
        this._actions.forEach(a => {
            actionManager.unregisterAction(a);
        });

        this._actions = [];
    }

    private _getWidth(): number {
        const context = this._guiTexture.getContext();
        context.font = `${this._textblock.fontSize} ${this._textblock.fontFamily}`;
        const measure = context.measureText(this._textblock.text);

        return measure.width
            + this._textblock.paddingLeftInPixels
            + this._textblock.paddingRightInPixels
            + this.thickness * 2;
    }

    private _getHeight(): number {
        return this._textblock.fontSizeInPixels
            + this._textblock.paddingTopInPixels
            + this._textblock.paddingBottomInPixels
            + this.thickness * 2;
    }

    private _updateVisibility(): void {
        if (this.options.visibility == TooltipVisibility.Always) {
            this.isVisible = true;
            this._unbind(this._actionManager);
        }
        else {
            this.isVisible = false;
            this._bind(this._actionManager);
        }
    }

    public static get animations(): Array<Animation> {
        if (Tooltip._animations.length == 0) {
            const alphaAnimation = new Animation('alphaAnimation', 'alpha', 50, Animation.ANIMATIONTYPE_FLOAT, Animation.ANIMATIONLOOPMODE_CONSTANT);
            const keys = [
                {
                    frame: 0,
                    value: 0
                },
                {
                    frame: 20,
                    value: 0
                },
                {
                    frame: 25,
                    value: 1
                }
            ];

            alphaAnimation.setKeys(keys);

            Tooltip._animations = [alphaAnimation];
        }

        return Tooltip._animations;
    }

    private _options: ITooltipStyle;
    public get options(): ITooltipStyle {
        return this._options;
    }

    public set options(value: ITooltipStyle) {
        this.color = ColorHelper.ParseColorToHexa(value.color);
        this._textblock.fontSize = value.size;
        this._options = value;
        this._updateVisibility();
        this._setPosition(value);
    }

    public set visibility(value: TooltipVisibility) {
        if (value != this.options?.visibility) {
            this.options.visibility = value;
            this._updateVisibility();
        }
    }

    private readonly _anchor: Mesh;
    private readonly _actionManager: AbstractActionManager;
    private readonly _guiTexture: AdvancedDynamicTexture;
    private readonly _textblock: TextBlock;
    private static _animations: Array<Animation> = [];
    private _actions: Array<Action> = [];
}
